
import { Attachment, FileGenerationStatus, Message, MessageRole, MessageDeliveryStatus, Mention } from '../types/chat';
import { CitationMarker } from './citation/CitationMarker';
import { rehypeCitations } from './citation/rehype-citations';
import {
  getCitedPage,
  highlightSourcePill,
  pillDomId,
  resolvePillByCitationIndex,
} from '../utils/source-citations';
import { useOpenSourceFile } from '../utils/use-open-source-file';
import { parseMessageTimeForDisplay } from '../utils/message-show-time';
import { CustomGpt } from '../types/custom-gpt.types';
import { CHAT_MAIN_COLUMN_CLASS } from 'constant/chat-layout';
import { CustomGptHeader } from './CustomGptHeader';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, FileText, Loader2, RotateCw } from 'lucide-react';
import { Copy } from 'iconoir-react';
import {
    CHAT_MESSAGE_ACTION_ICON_PROPS,
} from 'constant/chat-layout';
import { Button } from '../../../components/ui/button';
import seliseDiceLogo from '../../../assets/images/Group 193.svg';
import { getConnectorBadgeInfo, CONNECTOR_BADGE_CLASS, CONNECT_ACTION_TEXT } from '../utils/connector-link-badges';
import { ConnectorLinkCard } from './connector-link-card';
import { cn } from 'lib/utils';

import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { modelSupportedExtensionsMap, ModelTypes } from '../types/model.types';
import { ImagePreview } from './ImagePreview';
import { useToast } from '../../../hooks/use-toast';
import ExpandableThinkContainer from './ExpandableThinkContainer';
import { useTranslation } from 'react-i18next';
import { Feedback } from './Feedback';
import { useTheme } from '../../../components/core/theme-provider';
import { ChangeMessage } from './ChangeMessage';
import { AssistantStreamingRow, shouldRenderAssistantStreamingRow } from './AssistantStreamingRow';
import { ScrollToBottomControl } from './ScrollToBottomControl';
import { isAwayFromBottom } from '../utils/scroll-request-visibility';
import {
    hasInlineStreamSteps,
    mergeStreamStatusEntriesFromGenerations,
    resolveStreamActivityEntries,
} from '../utils/stream-status-entries';
import { AssistantAttachmentPreview } from './AssistantAttachmentPreview';
import { MarkdownChatImage } from './markdown-chat-image';
import { TryAgentBadgesList } from './TryAgentBadge';
import { MessageA2uiSurfaces } from './a2ui/MessageA2uiSurfaces';
import {
    conversationTurnHasPendingApproval,
    isLatestApprovalTurn,
} from '../utils/approval-conversation-messages';
import { generationHasPendingApproval, hasStepsStreamBlock } from '../utils/stream-blocks';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../components/ui/table';
import userParticipant from 'assets/images/userParticipant.png'
// import replyIcon from 'assets/images/reply.svg'
// import commentIcon from 'assets/images/comment.svg'



import { useChatStore } from '../../../state/store/chat/use-chat-store';
import { useConfig } from '../../../hooks/use-config';
import SourceFile from './SourceFile';
import { getDisplayableSources } from '../utils/source-display';
import { isValidUrl, placeHrefInContent } from 'utils/chat-utilities';
import { SearchSources } from './SearchSources';
import SearchRelatedList from './SearchRelatedList';
import SearchRelatedImageList from './SearchRelatedImageList';
import SearchRelatedVideoList from './SearchRelatedVideoList';
import { AnalyzingIndicator } from './AnalyzingIndicator';
import { StreamActivityDropdown } from './StreamActivityDropdown';
import { InterleavedStreamContent } from './InterleavedStreamContent';
import { CancelledResponseNotice } from './CancelledResponseNotice';
import { trackEvent } from 'analytics/tracker';
import { EVENTS } from 'analytics/events';


// import { useLocation } from 'react-router-dom';
// import { hasValidGroupChatComponentRoute } from 'pages/services/utilityService';
// import {
//     DropdownMenu,
//     DropdownMenuContent,
//     DropdownMenuGroup,
//     DropdownMenuItem,
//     DropdownMenuTrigger,
// } from '../../../components/ui/dropdown-menu';
import { formatDate } from '../../../utils/custom-date';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../../../components/ui/tooltip';
import ChatEvents from './ChatEvents';
import { deduplicateGroupChatMessages } from '../utils/deduplicate-group-chat-messages';
import { IamData } from '/features/iam/services/user-service';
import ReplyButton from './ReplyButton';
import ReplyBubble from './ReplyBubble';
// import { formatDate } from '/utils/custom-date';
// import { useModelStore } from 'state/store/chat/use-model-store';
// import { useAuthStore } from '../../../state/store/auth/useAuthStore';


// import ThinkingLoader from './ThinkingLoader';
// import eagleApp from 'assets/images/eagleApp.png'


const formatCalendarDayUtc = (d: Date): string =>
    new Intl.DateTimeFormat('en-CA', {
        timeZone: 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(d);

/**
 * Shows the instant in UTC (e.g. 3:21 AM). Same moment is 6 hours later in Bangladesh (GMT+6).
 */
const formatUtcDateTime = (epoch: number): string => {
    const date = new Date(epoch);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const now = new Date();
    const isToday = formatCalendarDayUtc(date) === formatCalendarDayUtc(now);

    const time = new Intl.DateTimeFormat('en-US', {
        timeZone: 'UTC',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).format(date);

    if (isToday) {
        return `Today at ${time}`;
    }

    const datePart = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'UTC',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(date);

    return `${datePart}, ${time}`;
};


const formatDayAndTime = (date: Date): string => {
    // const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    // const dayName = days[date.getDay()];

    const hours = date.getHours();
    const minutes = date.getMinutes();
    // const ampm = hours >= 12 ? 'pm' : 'am';
    // hours = hours
    // hours = hours

    const minutesStr = minutes.toString().padStart(2, '0');
    return `${hours}:${minutesStr}`;
};

const formatTimeWithAMPM = (date: Date): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const day = days[date.getDay()];
    const minutesStr = minutes.toString().padStart(2, '0');
    return `${day} ${displayHours}:${minutesStr} ${ampm}`;
};

interface ChatMessagesProps {
    messages: Message[];
    isLoading: boolean;
    streamingMessage: Message | undefined;
    isStillGenerating: boolean;
    chatInputHeight?: number;
    onSendMessage?: (content: string, files: Attachment[]) => void;
    currentUser?: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
        profileImageUrl?: string;
    } | null;
    hasParticipants?: boolean;
    participants?: IamData[];
    removedParticipants?: IamData[];
    associatedAssistant?: CustomGpt | null;
    scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
}

export const GroupChatMessages = ({
    messages,
    isLoading,
    streamingMessage,
    isStillGenerating,
    chatInputHeight = 186,
    onSendMessage,
    currentUser,
    participants,
    removedParticipants,
    associatedAssistant,
    scrollContainerRef,
}: ChatMessagesProps) => {
    const { t } = useTranslation();
    const { configurationSettings } = useConfig();
    // const selectedModel = useModelStore((state) => state.selectedModel);
    // console.log('streamingMessage', streamingMessage)
    // console.log('messages in groupChatMessages', messages, "streamingMessage", streamingMessage)



    // const currentUser = useAuthStore((state: any) => state.user);


    // console.log('streamingMessage in groupChatMessages in groupChatMessages', messages)
    // Use participants from current conversation instead of global hasParticipantsArray
    const activeConversationId = useChatStore((state) => state.activeConversationId);
    const isNewChat = useChatStore((state) => state.isNewChat);
    const currentConversation = useChatStore((state) =>
        activeConversationId ? state.conversations[activeConversationId] : null
    );
    const hasParticipantsArray = currentConversation?.participants || [];
    const hasRemovedParticipantsArray = currentConversation?.removedParticipants || [];

    const hasParticipants = hasParticipantsArray && hasParticipantsArray?.length > 0
    const displayMessages = useMemo(
        () => deduplicateGroupChatMessages(messages),
        [messages]
    );
    const latestRequestId = useMemo(
        () =>
            [...displayMessages]
                .reverse()
                .find((message) => message.role === MessageRole.USER_CHAT)?.recordId,
        [displayMessages]
    );

    const latestAgentRecordId = useMemo(
        () =>
            [...displayMessages]
                .reverse()
                .find(
                    (message) =>
                        message.role === MessageRole.ASSISTANT ||
                        message.role === MessageRole.ASSISTANT_CHAT
                )?.recordId,
        [displayMessages]
    );

    // const setCommentMessage = useChatStore((state) => state.setCommentMessage);
    // const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
    const setReplyMessage = useChatStore((state) => state.setReplyMessage);


    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [showScrollControl, setShowScrollControl] = useState(false);
    const isUserAtBottomRef = useRef<boolean>(true);
    // const { pathname } = useLocation();
    const { toast } = useToast();
    const { theme } = useTheme();
    const { openSourceFile } = useOpenSourceFile();

    const isAtBottom = (): boolean => {
        if (!scrollContainerRef?.current) return true;
        const { scrollTop, clientHeight, scrollHeight } = scrollContainerRef.current;
        // Check if user is within 50px of the bottom
        return scrollHeight - (scrollTop + clientHeight) < 50;
    };

    const scrollToBottom = (smooth = false) => {
        const container = scrollContainerRef?.current;
        if (!container) return;
        // Scroll the message pane only. scrollIntoView walks ancestors and pulls the composer up.
        if (smooth && typeof container.scrollTo === 'function') {
            container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
            return;
        }
        container.scrollTop = container.scrollHeight;
    };

    const syncScrollControl = useCallback(() => {
        setShowScrollControl(isAwayFromBottom(scrollContainerRef?.current ?? null));
    }, [scrollContainerRef]);

    useLayoutEffect(() => {
        syncScrollControl();
    }, [syncScrollControl, displayMessages, isStillGenerating]);

    const wasStreamingRef = useRef(false);
    useEffect(() => {
        const streamingStarted = isStillGenerating && !wasStreamingRef.current;
        wasStreamingRef.current = isStillGenerating;
        if (isNewChat || !streamingStarted || displayMessages.length === 0) return;
        scrollToBottom();
    }, [isStillGenerating, isNewChat, displayMessages.length]);

    // Auto-scroll when new messages arrive
    useEffect(() => {
        if (isNewChat || displayMessages.length === 0) return;
        const lastMessage = displayMessages[displayMessages.length - 1];
        const isUserMessage = lastMessage.role === MessageRole.USER_CHAT;
        const isCurrentUserMessage = isUserMessage && lastMessage.senderId === currentUser?.id;

        // Always scroll to bottom if current user sent a message, otherwise only if user is at bottom
        if (isCurrentUserMessage || isUserAtBottomRef.current) {
            requestAnimationFrame(() => {
                scrollToBottom();
                // Update ref to true when we auto-scroll for user message
                if (isCurrentUserMessage) {
                    isUserAtBottomRef.current = true;
                }
            });
        }
    }, [displayMessages.length, displayMessages[displayMessages.length - 1]?.recordId, currentUser?.id, isNewChat]);

    // Auto-scroll during streaming (only if user is at bottom)
    // useEffect(() => {
    //     if (isNewChat || !isStillGenerating) return;
    //
    //     // During streaming, continuously scroll if user is at bottom
    //     const scrollInterval = setInterval(() => {
    //         if (isUserAtBottomRef.current && isAtBottom()) {
    //             scrollToBottom();
    //         }
    //     }, 150); // Check every 150ms during streaming
    //
    //     return () => clearInterval(scrollInterval);
    // }, [isStillGenerating, isNewChat]);

    // Stop in-flight smooth scroll when this list unmounts (e.g. New Chat).
    useEffect(() => {
        const container = scrollContainerRef?.current;
        return () => {
            // Re-assigning scrollTop cancels any in-flight smooth scroll.
            if (container) {
                const { scrollTop } = container;
                container.scrollTop = scrollTop;
            }
        };
    }, [scrollContainerRef]);

    useEffect(() => {
        const handleScroll = () => {
            if (scrollContainerRef?.current) {
                isUserAtBottomRef.current = isAtBottom();
            }
            syncScrollControl();
        };

        const container = scrollContainerRef?.current;
        if (!container) return undefined;

        container.addEventListener('scroll', handleScroll);
        const observer = new ResizeObserver(() => syncScrollControl());
        observer.observe(container);
        if (container.firstElementChild instanceof HTMLElement) {
            observer.observe(container.firstElementChild);
        }

        return () => {
            container.removeEventListener('scroll', handleScroll);
            observer.disconnect();
        };
    }, [scrollContainerRef, syncScrollControl]);

    const fallbackCopyToClipboard = (text: string) => {
        const tempTextArea = document.createElement('textarea');
        tempTextArea.value = text;
        document.body.appendChild(tempTextArea);
        tempTextArea.select();

        try {
            document.execCommand('copy');
        } catch (err) {
            console.error('Failed to copy using execCommand: ', err);
        } finally {
            document.body.removeChild(tempTextArea);
        }
    };

    const handleCopyToClipboard = (text: string) => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text).then(() => {
                    console.log('Text copied to clipboard: ', text);
                });
            } else {
                fallbackCopyToClipboard(text);
            }
            toast({
                variant: 'success',
                title: t('COPIED'),
                description: t('TEXT_COPY_SUCCESS'),
            });
        } catch (err) {
            console.error('Failed to copy text: ', err);
            toast({
                variant: 'destructive',
                title: t('FAILED_TO_COPY_TEXT'),
                description: t('FAILED_COPY_TO_CLIPBOARD'),
            });
        }
    };

    const copyImageToClipboard = async (fileId: string) => {
        try {
            const itemIdFilePathMap = useChatStore.getState().itemIdFilePathMap;
            const imageSrc = itemIdFilePathMap.get(fileId);

            if (!imageSrc) {
                toast({
                    variant: 'destructive',
                    title: t('FAILED_TO_COPY_IMAGE'),
                    description: t('FAILED_COPY_IMAGE_TO_CLIPBOARD'),
                });
                return;
            }

            try {
                const response = await fetch(imageSrc);
                if (!response.ok) {
                    trackEvent(EVENTS.ERROR(new Error("Failed to copy image"), {
                        endpoint: imageSrc,
                        method: 'GET',
                        status: response.status
                    }));
                }
                const blob = await response.blob();
                const imageBlob = new Blob([blob], { type: 'image/png' });

                await navigator.clipboard.write([
                    new ClipboardItem({
                        'image/png': imageBlob,
                    }),
                ]);

                toast({
                    variant: 'success',
                    title: t('COPIED'),
                    description: t('IMAGE_COPY_SUCCESS'),
                });
                return;
            } catch (clipboardError) {
                console.warn('Clipboard API failed, falling back to download:', clipboardError);
            }
        } catch (error) {
            console.error('Failed to copy/download image:', error);
            toast({
                variant: 'destructive',
                title: t('FAILED_TO_COPY_IMAGE'),
                description: t('FAILED_COPY_IMAGE_TO_CLIPBOARD'),
            });
        }
    };

    const copyChatBubbleIcons = [
        {
            icon: <Copy {...CHAT_MESSAGE_ACTION_ICON_PROPS} />,
            onClick: async (text: string, hasImage?: boolean, fileId?: string) => {
                if (hasImage && fileId && navigator.clipboard && window.isSecureContext) {
                    await copyImageToClipboard(fileId);
                } else handleCopyToClipboard(text);
            },
            type: 'icon',
            tooltip: t('COPY'),
        },
    ];

    const escapeHtml = (value: string): string => {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    };

    const hasTripleBackticks = (value: string): boolean => value.includes('```');

    /** Backend/LLM often returns <code>...</code> instead of markdown fences. */
    const normalizeHtmlCodeBlocks = (content: string): string => {
        if (!/<code[\s>]/i.test(content)) return content;
        return content.replace(/<code>([\s\S]*?)<\/code>/gi, (_match, code: string) => {
            const trimmed = code.trim();
            const language = /\bimport\s+pandas\b/i.test(trimmed) ? 'python' : '';
            return `\n\`\`\`${language}\n${trimmed}\n\`\`\`\n`;
        });
    };

    const markdownCodeText = (children: unknown): string => {
        if (typeof children === 'string') return children;
        if (Array.isArray(children)) {
            return children.map((child) => (typeof child === 'string' ? child : '')).join('');
        }
        return String(children ?? '');
    };

    const escapeRegex = (value: string): string => {
        return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    const buildMentionLabels = (): string[] => {
        const allUsers = [...(participants || []), ...(removedParticipants || [])];
        const labels = allUsers
            .map((u) => `${u.firstName ?? ''}${u.lastName ? ` ${u.lastName}` : ''}`.trim())
            .filter((x) => Boolean(x));

        // Include model mention
        labels.push('SeliseAI');

        // Deduplicate (case-insensitive)
        const seen = new Set<string>();
        return labels.filter((l) => {
            const key = l.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    };

    const applyMentionHighlighting = (content: string, mentions?: Mention[]): string => {
        // If content already contains HTML entities or tags, skip processing to avoid double-encoding
        // Check for both encoded HTML entities and actual HTML tags
        if (content.includes('&lt;') || content.includes('&gt;') || content.includes('<span') || content.includes('</span>')) {
            return content;
        }

        // Prefer backend ranges when present
        if (!mentions || mentions.length === 0) {
            // Fallback: if we have a code block, don't apply regex highlighting (can break formatting).
            // If mention ranges exist, they are still handled above.
            if (hasTripleBackticks(content)) return content;

            // Best-effort fallback for persisted messages that don't include mention ranges:
            // Try matching full display names (including spaces) from known participants first.
            const mentionLabels = buildMentionLabels().sort((a, b) => b.length - a.length);
            if (mentionLabels.length > 0) {
                const labelAlternation = mentionLabels
                    .map((label) => escapeRegex(label).replace(/\s+/g, '\\s+'))
                    .join('|');

                if (labelAlternation) {
                    const fullNameRegex = new RegExp(
                        `(^|[\\s(])@(${labelAlternation})(?=$|[\\s\\n\\t.,!?;:)])`,
                        'gi'
                    );

                    const replaced = content.replace(
                        fullNameRegex,
                        (_match, prefix: string, name: string) => {
                            // keep the exact matched casing/spaces from the message
                            const safe = escapeHtml(String(name));
                            return `${prefix}<span class="font-semibold text-primary dark:text-[#FFFFFF]">@${safe}</span>`;
                        }
                    );

                    if (replaced !== content) return replaced;
                }
            }

            // Fallback fallback: highlight @token (single word)
            const tokenRegex = /(^|[\s(])@([A-Za-z0-9._-]+)/g;
            return content.replace(tokenRegex, (_match, prefix: string, name: string) => {
                return `${prefix}<span class="font-semibold text-primary dark:text-[#FFFFFF]">@${escapeHtml(
                    String(name)
                )}</span>`;
            });
        }

        // Build result by processing original content and inserting spans at mention positions
        // First, deduplicate mentions - remove only exact duplicates (same start, end, and userId)
        const uniqueMentions: Mention[] = [];
        const seenRanges = new Set<string>();

        for (const mention of mentions) {
            // Create a key for this mention range (exact position)
            const rangeKey = `${mention.start}-${mention.end}-${mention.userId}`;

            // Only skip if we've already processed this EXACT range (same position)
            // Allow multiple mentions of the same user at different positions
            if (!seenRanges.has(rangeKey)) {
                uniqueMentions.push(mention);
                seenRanges.add(rangeKey);
            }
        }

        // Sort mentions by start position (ascending) to process in order
        const sortedMentions = uniqueMentions
            .filter(m => m.start >= 0 && m.end <= content.length && m.start < m.end)
            .sort((a, b) => a.start - b.start);

        if (sortedMentions.length === 0) {
            return content;
        }

        let result = '';
        let lastIndex = 0;

        // Helper function to highlight @mentions in a text segment
        const highlightMentionsInText = (text: string): string => {
            if (!text) return text;
            // Match @ followed by word characters (alphanumeric, underscore, dots, hyphens)
            const mentionRegex = /@([A-Za-z0-9._-]+)/g;
            return text.replace(mentionRegex, (match, name) => {
                const safe = escapeHtml(name);
                return `<span class="font-semibold text-primary dark:text-[#FFFFFF]">@${safe}</span>`;
            });
        };

        sortedMentions.forEach((mention) => {
            // Expand to include a preceding '@' if it's outside the provided range
            const adjustedStart =
                mention.start > 0 && content[mention.start - 1] === '@' ? mention.start - 1 : mention.start;

            // Add text before this mention and highlight any @mentions in it
            if (adjustedStart > lastIndex) {
                const textBefore = content.substring(lastIndex, adjustedStart);
                result += highlightMentionsInText(textBefore);
            }

            // Extract mention text from ORIGINAL content (not processed)
            const mentionText = content.substring(adjustedStart, mention.end);
            const safeMentionText = escapeHtml(mentionText);

            // Add the highlighted mention
            result += `<span class="font-semibold text-primary dark:text-[#FFFFFF]">${safeMentionText}</span>`;

            lastIndex = mention.end;
        });

        // Add remaining text after last mention and highlight any @mentions in it
        if (lastIndex < content.length) {
            const textAfter = content.substring(lastIndex);
            result += highlightMentionsInText(textAfter);
        }

        return result;
    };


    const messageBubbleUi = (
        message: Message,
        index: number,
        hideButtons?: boolean,
        isLatestTurn = true,
    ) => {

        const generation = message.generations[index];
        // console.log('generation in messageBubbleUi', generation)
        // console.log('generation', generation)
        // can we use this code {"step_type": "action", "tool": "python_interpreter", "code": "import pandas as pd\nimport matplotlib.pyplot as plt\n\n# Read the Excel file into a DataFrame\ntry:\n df = pd.read_excel('/tmp/code_interpreter_rusgt571/random_people_200plus.xlsx')\nexcept Exception as e:\n print(f"Failed to read Excel file: {e}")\n # Handle the exception appropriately\n\n# Check if the DataFrame is not empty\nif not df.empty:\n # Assuming there's a column named 'values' or a similar numeric column\n numeric_columns = df.select_dtypes(include=['int64', 'float64']).columns\n \n if len(numeric_columns) > 0:\n # Use the first numeric column found for analysis\n value_column = numeric_columns[0]\n \n # Get the top 10 values\n top_10 = df.nlargest(10, value_column)\n \n # Create a bar chart\n plt.figure(figsize=(10,6))\n plt.bar(top_10.index, top_10[value_column])\n plt.xlabel('Index')\n plt.ylabel(value_column)\n plt.title(f'Top 10 {value_column}')\n plt.xticks(rotation=90) # Rotate x-axis labels for better readability\n \n # Save the plot\n plt.savefig('/tmp/code_artifacts_cchg7pnw/top_10_values_plot.png')\n plt.close()\n \n print("Top 10 values and plot created successfully.")\n else:\n print("No numeric columns found in the data.")\nelse:\n print("The DataFrame is empty.")", "observations": "Execution logs:\nTop 10 values and plot created successfully.\nLast output from code snippet:\n(array([-20., 0., 20., 40., 60., 80., 100.]), [Text(-20.0, 0, '\u221220'), Text(0.0, 0, '0'), Text(20.0, 0, '20'), Text(40.0, 0, '40'), Text(60.0, 0, '60'), Text(80.0, 0, '80'), Text(100.0, 0, '100')])"}}
        // in react markdown


        if (generation.searchSources) {
            generation.content = placeHrefInContent(generation.content, generation.searchSources);
            generation.searchSources = generation.searchSources.filter((source) => {
                return source.metadata?.url && isValidUrl(source.metadata.url);
            });
        }

        const normalizedContent = normalizeHtmlCodeBlocks(generation.content || '');

        // Add code execution steps to content if they exist


        const assistantAttachments = generation.attachments?.filter(
            (attachment) => attachment.sender === 'assistant'
        );
        const assistantGeneratedImages = generation.generatedImages?.filter(
            (attachment) => attachment.sender === 'assistant'
        );

        const userAttachments = generation.attachments?.filter(
            (attachment) => attachment.sender === 'user'
        );
        const sources = generation?.sources || [];

        // Inline [[ref:n]] citation markers for KB/document answers.
        const citationSources = generation?.sources ?? [];
        const isAssistantRole =
          message.role === MessageRole.ASSISTANT || message.role === MessageRole.ASSISTANT_CHAT;
        const hasCitations = isAssistantRole && citationSources.length > 0;
        const handleCitationClick = (n: number) => {
          const source = resolvePillByCitationIndex(citationSources, n);
          if (!source) return;
          openSourceFile(source, getCitedPage(source));
          highlightSourcePill(pillDomId(generation.id, source));
        };

        const handleSendMessage = (message: string, files: any[]) => {
            onSendMessage?.(message, files);
        };

        const streamActivityEntries = resolveStreamActivityEntries(generation);
        const usesInlineStreamSteps = hasInlineStreamSteps(generation.streamStatusEntries);
        const isGenerationStreaming =
          !generation.isCancelled && !!(generation.isThisGenerationOnGoing ?? false);
        // History without offsets: one Steps above the answer. Live turns use interleaved Steps.
        const showStreamActivity =
            message.role === MessageRole.ASSISTANT &&
            !!generation?.content?.trim() &&
            streamActivityEntries.length > 0 &&
            !isGenerationStreaming &&
            !usesInlineStreamSteps &&
            !hasStepsStreamBlock(generation.streamBlocks);

        // Helper function to format user name from firstName and lastName
        const formatUserName = (
            firstName?: string,
            lastName?: string | null,
            email?: string
        ): string => {
            const trimmedFirst = firstName?.trim();
            if (trimmedFirst) {
                const trimmedLast = lastName?.trim();
                return trimmedLast ? `${trimmedFirst} ${trimmedLast}` : trimmedFirst;
            }
            if (email) {
                return email.split('@')[0] || 'User';
            }
            return 'User';
        };

        const idsMatch = (a?: string, b?: string): boolean => {
            if (!a || !b) return false;
            if (a === b) return true;
            const norm = (id: string) => (id.includes('|') ? id.slice(id.indexOf('|') + 1) : id);
            return norm(a) === norm(b);
        };

        /** Human messages that can trigger a Selise AI reply (own + other participants). */
        const isUserPromptMessage = (role: MessageRole): boolean =>
            role === MessageRole.USER_CHAT || role === MessageRole.ASSISTANT_CHAT;

        const findUserMessageByRefIds = (refIds: string[]): Message | undefined => {
            for (const refId of refIds) {
                if (!refId) continue;
                for (let i = messages.length - 1; i >= 0; i -= 1) {
                    const msg = messages[i];
                    if (!isUserPromptMessage(msg.role)) continue;
                    if (idsMatch(msg.recordId, refId)) return msg;
                    if (msg.generations.some((g) => idsMatch(g.id, refId))) return msg;
                }
            }
            return undefined;
        };

        const resolveNameForSender = (senderId?: string): string => {
            if (!senderId) return 'User';

            if (currentUser?.id && idsMatch(senderId, currentUser.id)) {
                return formatUserName(currentUser.firstName, currentUser.lastName, currentUser.email);
            }

            const participantLists = [
                participants,
                removedParticipants,
                hasParticipantsArray,
                hasRemovedParticipantsArray,
            ];

            for (const list of participantLists) {
                const match = list?.find((p) => idsMatch(p.itemId, senderId));
                if (match) {
                    const name = formatUserName(match.firstName, match.lastName, match.email);
                    if (name !== 'User') return name;
                }
            }

            return 'User';
        };

        // Get user message data for assistant messages (shows which user triggered the response)
        const getUserMessageData = (): { userName: string; message: string; createdDate?: number; showMessageTime?: string } => {
            if (message.role !== MessageRole.ASSISTANT) {
                return { userName: '', message: '' };
            }

            const refIds = [
                generation.parentUserGenerationId,
                message.userRecordId,
            ].filter(Boolean) as string[];

            // Link to the exact user turn that @mentioned Selise AI / sent the prompt (generation id from API)
            let userRecord = findUserMessageByRefIds(refIds);

            if (!userRecord) {
                const assistantIdx = messages.findIndex((m) => m.recordId === message.recordId);
                if (assistantIdx > 0) {
                    for (let i = assistantIdx - 1; i >= 0; i -= 1) {
                        if (isUserPromptMessage(messages[i].role)) {
                            userRecord = messages[i];
                            break;
                        }
                    }
                }
            }

            if (!userRecord) {
                return { userName: 'User', message: '' };
            }

            const matchedGeneration =
                userRecord.generations?.find((g) => refIds.some((refId) => idsMatch(g.id, refId))) ??
                userRecord.generations[userRecord.activeGenerationIndex ?? 0] ??
                userRecord.generations[0];

            const userName = resolveNameForSender(userRecord.senderId);

            return {
                userName,
                message: matchedGeneration?.content || '',
                createdDate: matchedGeneration?.createdDate,
                showMessageTime: matchedGeneration?.showMessageTime,
            };
        };

        const userMessageData = getUserMessageData();

        return <>
            {generation.parentMessage && (
                <ReplyBubble message={message} generation={generation} />
            )}
            <div className={` ${message.role === MessageRole.USER_CHAT ? ' min-w-full' : 'w-full flex flex-col'}  mb-1`}>
                <div className={`flex items-center min-w-0 ${message.role === MessageRole.USER_CHAT ? 'justify-end' : 'justify-start'} group`}>

                    {message.role === MessageRole.USER_CHAT && (
                        <>
                            {/* Resend button for failed messages */}
                            {message.deliveryStatus === MessageDeliveryStatus.FAILED && message.senderId === currentUser?.id && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                className="w-4 h-4 mr-2 rounded-full cursor-pointer hover:opacity-70 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto focus:opacity-100 focus:pointer-events-auto"
                                                onClick={async () => {
                                                    const retryFailedMessage = useChatStore.getState().retryFailedMessage;
                                                    const activeConversationId = useChatStore.getState().activeConversationId;

                                                    if (retryFailedMessage && activeConversationId && configurationSettings) {
                                                        await retryFailedMessage(message.recordId, configurationSettings, activeConversationId);
                                                    }
                                                }}
                                            >
                                                <RotateCw className={`w-4 h-4 ${theme === 'dark' ? 'text-primary group-hover:text-primary-50' : 'text-primary group-hover:text-primary-50'}`} />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>{t('RESEND_MESSAGE') || 'Resend message'}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}

                            <ReplyButton hasParticipantsArray={hasParticipantsArray} hasRemovedParticipantsArray={hasRemovedParticipantsArray} message={message} generation={generation} setReplyMessage={setReplyMessage} />
                            {/* <DropdownMenu
                                modal={false}
                                open={openDropdownId === generation.id}
                                onOpenChange={(open) => setOpenDropdownId(open ? generation.id : null)}
                            >
                                <DropdownMenuTrigger asChild>
                                    <button className="w-4 h-4 mr-2 rounded-full object-cover cursor-pointer hover:opacity-70 transition-opacity">
                                        <EllipsisVertical className="w-4 h-4" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="w-40 rounded-lg p-2 shadow-lg border bg-background" align="start">
                                    <DropdownMenuGroup>
                                        <DropdownMenuItem onClick={() => {
                                            const repliedUser = hasParticipantsArray.find(participant => participant.itemId === message.senderId || currentUser?.id)
                                                || hasRemovedParticipantsArray.find(participant => participant.itemId === message.senderId || currentUser?.id);
                                            // console.log('Reply to message:', repliedUser);
                                            const replyMessageData: ReplyMessageData = {
                                                generation: generation,
                                                repliedUser: repliedUser,
                                            };
                                            setReplyMessage(replyMessageData);
                                            setOpenDropdownId(null);
                                        }}>
                                            <img src={replyIcon} alt="reply" className="w-4 h-4 mr-2" />
                                            <span className={`text-sm font-normal ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[text-[#555555]]'}`}>
                                                Reply
                                            </span>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => {
                                            setCommentMessage(message);
                                            setOpenDropdownId(null);
                                        }}>
                                            <img src={commentIcon} alt="copy" className="w-4 h-4 mr-2" />
                                            <span className={`text-sm font-normal ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[text-[#555555]]'}`}>
                                                Comment
                                            </span>
                                        </DropdownMenuItem>
                                    </DropdownMenuGroup>
                                </DropdownMenuContent>
                            </DropdownMenu> */}
                        </>
                    )}

                    <div
                        className={`${(message.role === MessageRole.USER_CHAT || message.role === MessageRole.ASSISTANT_CHAT) ? (theme === 'dark' ? 'rounded-[12px] bg-[#27272C] p-2' : 'rounded-[12px] bg-[#F6F6F6] px-[13px] py-[14px] mt-[-10px] z-20') : ''} max-w-[100%]`}
                    >
                        <div className="break-words min-w-0 text-sm">
                            {/* user message that triggered this assistant response */}
                            {userMessageData && message.role === MessageRole.ASSISTANT && (
                                <div className={`py-3.5 px-3 rounded-[20px] w-fit max-w-full mb-2 ${theme === 'dark'
                                    ? 'bg-primary-600'
                                    : 'bg-[#F6F6F6]'
                                    }`}>
                                    <p className="text-sm leading-relaxed text-foreground line-clamp-2 text-start">
                                        {userMessageData.message}
                                    </p>
                                    <div className="flex items-center justify-end gap-2 mt-2">

                                        <span className={`text-xs font-semibold ${theme === 'dark' ? 'text-[#27272C]' : 'text-[#27272C]'}`}>
                                            {userMessageData.userName}
                                        </span>
                                        <span className={`text-xs font-semibold ${theme === 'dark' ? 'text-[#BCBCBE]' : 'text-[#9C9C9E]'}`}>
                                            {(() => {
                                                const d = parseMessageTimeForDisplay(userMessageData.showMessageTime, userMessageData.createdDate);
                                                return d ? formatDate(d) : '';
                                            })()}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {(message.role === MessageRole.USER_CHAT) && userAttachments && userAttachments.length > 0 && (
                                <div className="flex flex-col items-end justify-end gap-2 mb-4">
                                    {userAttachments.map((file) => {
                                        if (
                                            modelSupportedExtensionsMap[ModelTypes.VISION].image.includes(
                                                `.${file.extension}`
                                            )
                                        ) {
                                            return <ImagePreview key={file.fileId} itemId={file.fileId} />;
                                        }

                                        return (
                                            <div
                                                key={file.fileId}
                                                className="flex items-center justify-between bg-gray-50 p-2 rounded mt-2"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <FileText className="h-4 w-4 text-gray-500" />
                                                    <span className="text-sm text-black truncate max-w-[180px]">
                                                        {file.fileName}
                                                    </span>
                                                </div>
                                                {file.sender === 'assistant' &&
                                                    file.status &&
                                                    file.status === FileGenerationStatus.IN_PROGRESS && (
                                                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                                    )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {generation.searchSources && generation.searchSources.length > 0 && <SearchSources sources={generation.searchSources} />}
                            {generation.thinkContent && (
                                <ExpandableThinkContainer
                                    content={generation.thinkContent ?? ''}
                                    isThinking={generation.isThinking}
                                    className='mb-2'
                                />
                            )}
                            {generation.isAnalyzing && !(generation.thinkContent && (generation.isThinking || !generation.content)) &&
                                <AnalyzingIndicator isAnalyzingStep={false} isAnalyzing={true} />}
                            {generation.isAnalyzed &&
                                <AnalyzingIndicator
                                    isAnalyzingStep={true}
                                    isAnalyzing={false}
                                    codeExecutionSteps={(generation as any).codeExecutionSteps}
                                />}
                            {showStreamActivity && (
                                <StreamActivityDropdown entries={streamActivityEntries} />
                            )}
                            {/* {generation.content && message.toolsStatus && message.toolsStatus[Tools.SEARCH] && (
            <span className="inline-block italic bg-[#e9e9e980] px-2 py-1 rounded my-1">
              {t('SEARCH_RESULT')}:{' '}
            </span>
          )} */}
                            {generation.content === 'SeliseAI is typing...' ? (
                                <div className="flex items-center gap-2 py-2">
                                    <span className="text-sm italic text-primary  dark:text-[#FFFFFF] text-shimmer">
                                        SeliseAI is typing...
                                    </span>
                                </div>
                            ) : (
                                <InterleavedStreamContent
                                    content={normalizedContent}
                                    entries={
                                        usesInlineStreamSteps
                                            ? generation.streamStatusEntries
                                            : undefined
                                    }
                                    surfaces={generation.a2uiSurfaces}
                                    streamBlocks={generation.streamBlocks}
                                    isStreaming={isGenerationStreaming}
                                    renderText={(text) => (
                                        <ReactMarkdown
                                            remarkPlugins={[remarkGfm]}
                                            rehypePlugins={hasCitations ? [rehypeRaw, rehypeCitations] : [rehypeRaw]}
                                            components={{
                                                citationref: (props: any) => (
                                                    <CitationMarker
                                                        {...props}
                                                        sources={citationSources}
                                                        onCitationClick={handleCitationClick}
                                                    />
                                                ),
                                                h1: ({ children }: any) => (
                                                    <h1 className="text-3xl font-bold mb-2 last:mb-0">{children}</h1>
                                                ),
                                                h2: ({ children }: any) => (
                                                    <h2 className="text-2xl font-semibold mb-2 last:mb-0">{children}</h2>
                                                ),
                                                h3: ({ children }: any) => (
                                                    <h3 className="text-[18.72px] font-semibold mb-2 last:mb-0">{children}</h3>
                                                ),
                                                h4: ({ children }: any) => (
                                                    <h4 className="text-base font-medium mb-2 last:mb-0">{children}</h4>
                                                ),
                                                h5: ({ children }: any) => (
                                                    <h5 className="text-[13.28px] font-medium mb-2 last:mb-0">{children}</h5>
                                                ),
                                                h6: ({ children }: any) => (
                                                    <h6 className="text-[10.72px] font-medium mb-2 last:mb-0">{children}</h6>
                                                ),
                                                p: ({ children }: any) => <p className={`mb-2 last:mb-0 break-words text-sm font-normal py-2 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#27272C]'}`}>{children}</p>,
                                                ul: ({ children }: any) => <ul className="list-disc pl-4 mb-2">{children}</ul>,
                                                ol: ({ children, start }: any) => <ol start={start} className="list-decimal pl-4 mb-2 px-3 py-4">{children}</ol>,
                                                li: ({ children }: any) => <li className="mb-0.5">{children}</li>,
                                                code: ({ inline, className, children, ...props }: any) => {
                                                    const match = /language-(\w+)/.exec(className || '');
                                                    const codeText = markdownCodeText(children).replace(/\n$/, '');
                                                    const isBlock = !inline && (Boolean(match) || codeText.includes('\n'));
                                                    if (isBlock) {
                                                        const language =
                                                            match?.[1] ??
                                                            (/\bimport\s+pandas\b/i.test(codeText) ? 'python' : 'text');
                                                        return (
                                                            <SyntaxHighlighter
                                                                style={dracula}
                                                                language={language}
                                                                PreTag="pre"
                                                                customStyle={{
                                                                    backgroundColor: '#282C34',
                                                                    padding: '1rem',
                                                                    borderRadius: '0.375rem',
                                                                    overflowX: 'auto',
                                                                    fontSize: '0.875rem',
                                                                }}
                                                                {...props}
                                                            >
                                                                {codeText}
                                                            </SyntaxHighlighter>
                                                        );
                                                    }
                                                    return (
                                                        <code
                                                            className="rounded-[7px] border border-gray-300/60 bg-[#f0f1f3] px-1.5 py-0.5 font-mono text-sm text-[#ab5152] dark:border-gray-600 dark:bg-muted dark:text-[#fca5a5]"
                                                            {...props}
                                                        >
                                                            {codeText}
                                                        </code>
                                                    );
                                                },
                                                pre: ({ children }: any) => (
                                                    <pre className="w-full bg-gray-800 text-gray-100 rounded p-2 overflow-x-auto mb-2">
                                                        {children}
                                                    </pre>
                                                ),
                                                blockquote: ({ children }: any) => (
                                                    <blockquote className="border-l-4 border-gray-600 pl-4 italic mb-2">
                                                        {children}
                                                    </blockquote>
                                                ),
                                                table: ({ children }: any) => (
                                                    <div className="mb-2 overflow-x-auto">
                                                        <Table>{children}</Table>
                                                    </div>
                                                ),
                                                thead: ({ children }: any) => <TableHeader>{children}</TableHeader>,
                                                tbody: ({ children }: any) => <TableBody>{children}</TableBody>,
                                                tr: ({ children }: any) => <TableRow>{children}</TableRow>,
                                                th: ({ children }: any) => <TableHead>{children}</TableHead>,
                                                td: ({ children }: any) => <TableCell>{children}</TableCell>,
                                                a: ({ href, children }: any) => {
                                                    if (children == null || children === '') return null;

                                                    const extractText = (node: any): string => {
                                                        if (typeof node === 'string') return node;
                                                        if (typeof node === 'number') return String(node);
                                                        if (Array.isArray(node)) return node.map(extractText).join('');
                                                        if (node && typeof node === 'object' && node.props?.children) {
                                                            return extractText(node.props.children);
                                                        }
                                                        return '';
                                                    };

                                                    const text = extractText(children);
                                                    const connector = getConnectorBadgeInfo(text, href);

                                                    if (connector && connector.name !== 'Agent' && CONNECT_ACTION_TEXT.test(text)) {
                                                        return (
                                                            <ConnectorLinkCard href={href} name={connector.name} icon={connector.icon} description={text} />
                                                        );
                                                    }

                                                    return (
                                                        <a
                                                            href={href}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={cn(
                                                                'group mx-1 my-0.5 inline-flex items-center gap-1.5 align-middle rounded-full px-2.5 py-0.5 text-xs font-medium cursor-pointer shadow-xs transition-all duration-150 select-none no-underline active:scale-95 border border-gray-300 dark:border-gray-500',
                                                                connector
                                                                    ? connector.className
                                                                    : CONNECTOR_BADGE_CLASS
                                                            )}
                                                        >
                                                            {connector?.icon}
                                                            <span>{children}</span>
                                                            <ExternalLink className="h-3 w-3 shrink-0 opacity-70 transition-opacity group-hover:opacity-100" />
                                                        </a>
                                                    );
                                                },
                                                img: MarkdownChatImage,
                                            } as any}
                                        >
                                            {applyMentionHighlighting(text, generation.mentions)}
                                        </ReactMarkdown>
                                    )}
                                    renderSurfaces={(surfaces) => (
                                        <MessageA2uiSurfaces
                                            surfaces={surfaces}
                                            userMessageId={message.userRecordId ?? generation.parentUserGenerationId}
                                            assistantMessageId={generation.id}
                                            isLatest={isLatestTurn}
                                        />
                                    )}
                                />
                            )}
                            {generation.isCancelled && <CancelledResponseNotice />}
                            <TryAgentBadgesList changes={generation.platformResourceChanges} />
                        </div>

                    </div>
                    {message.role === MessageRole.ASSISTANT_CHAT && (

                        <ReplyButton hasParticipantsArray={hasParticipantsArray} hasRemovedParticipantsArray={hasRemovedParticipantsArray} message={message} generation={generation} setReplyMessage={setReplyMessage} />
                        // <DropdownMenu
                        //     modal={false}
                        //     open={openDropdownId === generation.id}
                        //     onOpenChange={(open) => setOpenDropdownId(open ? generation.id : null)}
                        // >
                        //     <DropdownMenuTrigger asChild>
                        //         <button className="w-4 h-4 ml-2 rounded-full object-cover cursor-pointer hover:opacity-70 transition-opacity">
                        //             <EllipsisVertical className="w-4 h-4" />
                        //         </button>
                        //     </DropdownMenuTrigger>
                        //     <DropdownMenuContent className="w-40 rounded-lg p-2 shadow-lg border bg-background" align="end">
                        //         <DropdownMenuGroup>
                        //             <DropdownMenuItem onClick={() => {
                        //                 const repliedUser = hasParticipantsArray.find(participant => participant.itemId === message.senderId || currentUser?.id);
                        //                 // console.log('Reply to message:', repliedUser);
                        //                 const replyMessageData: ReplyMessageData = {
                        //                     generation: generation,
                        //                     repliedUser: repliedUser,
                        //                 };
                        //                 setReplyMessage(replyMessageData);
                        //                 setOpenDropdownId(null);
                        //             }}>
                        //                 <img src={replyIcon} alt="reply" className="w-4 h-4 mr-2" />
                        //                 <span className={`text-sm font-normal ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[text-[#555555]]'}`}>
                        //                     Reply
                        //                 </span>
                        //             </DropdownMenuItem>
                        //             <DropdownMenuItem onClick={() => {
                        //                 console.log('Comment on message:', generation.id);
                        //                 setCommentMessage(message);
                        //                 setOpenDropdownId(null);
                        //             }}>
                        //                 <img src={commentIcon} alt="copy" className="w-4 h-4 mr-2" />
                        //                 <span className={`text-sm font-normal ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[text-[#555555]]'}`}>
                        //                     Comment
                        //                 </span>
                        //             </DropdownMenuItem>
                        //         </DropdownMenuGroup>
                        //     </DropdownMenuContent>
                        // </DropdownMenu>
                    )}
                </div>


                <>
                    {(message.role === MessageRole.ASSISTANT_CHAT || message.role === MessageRole.ASSISTANT) &&
                        assistantAttachments &&
                        assistantAttachments.length > 0 && (
                            <div className="flex w-full min-w-0 flex-wrap justify-start gap-2 mb-2">
                                {assistantAttachments.map((file) => (
                                    <AssistantAttachmentPreview key={file.fileId} file={file} hasParticipants={true} />
                                ))}
                            </div>
                        )}
                </>
                <>
                    {(message.role === MessageRole.ASSISTANT_CHAT || message.role === MessageRole.ASSISTANT) &&
                        assistantGeneratedImages &&
                        assistantGeneratedImages.length > 0 && (
                            <div className="flex w-full min-w-0 flex-wrap justify-start gap-2 mb-2">
                                {assistantGeneratedImages.map((file) => (
                                    <AssistantAttachmentPreview key={file.fileId} file={file} hasParticipants={true} />
                                ))}
                            </div>
                        )}
                </>
                <>
                    {(message.role === MessageRole.ASSISTANT_CHAT || message.role === MessageRole.ASSISTANT) &&
                        sources &&
                        sources.length > 0 && (
                            <div className="flex items-start justify-start gap-2 mb-2 ml-4">
                                <p className="text-[10px] font-semibold">{t('SOURCES')}:</p>
                                <div className="flex flex-wrap justify-start gap-2 mb-2">
                                    {sources.map((source) => (
                                        <SourceFile
                                            key={source.fileId}
                                            sourceFile={source}
                                            domId={pillDomId(generation.id, source)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                </>
                <div>
                    {(message.role == MessageRole.ASSISTANT_CHAT || message.role == MessageRole.ASSISTANT) && (
                        <>
                            {!hideButtons && (
                                <div className="flex  md:gap-2 lg:gap-2  justify-start">
                                    {!isGenerationStreaming && message.generations.length > 1 && (
                                        <ChangeMessage
                                            recordId={message.recordId}
                                            currentMessageIndex={message.activeGenerationIndex ?? 0}
                                            totalMessageCount={message.generations.length}
                                            isGenerating={isGenerationStreaming}
                                        />
                                    )}
                                    {!hasParticipants && copyChatBubbleIcons.map((icon) => (
                                        <button
                                            title={icon.tooltip}
                                            key={icon.type}
                                            className="flex items-center justify-center h-8 w-8 pl-0 pr-1.5 py-1.5rounded-full hover:bg-muted"
                                            onClick={async () =>
                                                await icon.onClick(
                                                    generation.content,
                                                    (generation.generatedImages ?? []).length > 0,
                                                    (generation.generatedImages ?? []).length > 0
                                                        ? (generation.generatedImages ?? [])[0].fileId
                                                        : undefined
                                                )
                                            }
                                        >
                                            {icon.icon}
                                        </button>
                                    ))}
                                    {
                                        message.role === MessageRole.ASSISTANT &&
                                        !hideButtons && (
                                            <Feedback
                                                recordId={message.userRecordId ?? ''}
                                                message={generation}
                                                isStillGenerating={
                                                    generation?.isThisGenerationOnGoing ?? false
                                                }
                                                hasParticipants={true}
                                                conversationResponseGenerating={isStillGenerating}
                                                hideRegenerateButton={
                                                    generationHasPendingApproval(generation) ||
                                                    conversationTurnHasPendingApproval(displayMessages, message.userRecordId)
                                                }
                                            />
                                        )
                                    }

                                </div>
                            )}
                        </>
                    )}

                </div>
                {message.role === MessageRole.ASSISTANT_CHAT || message.role === MessageRole.ASSISTANT && <SearchRelatedImageList isSkeletonShowForImages={generation.isSkeletonShowForImages} messageId={message.recordId} imageList={generation.relatedSearcheImageList} />}
                {message.role === MessageRole.ASSISTANT_CHAT || message.role === MessageRole.ASSISTANT && <SearchRelatedVideoList isSkeletonShowForVideos={generation.isSkeletonShowForVideos} messageId={message.recordId} videoList={generation.relatedSearcheVideoList} />}
                {message.role === MessageRole.ASSISTANT_CHAT || message.role === MessageRole.ASSISTANT && <SearchRelatedList isSkeletonShowForQuestions={generation.isSkeletonShowForQuestions} messageId={message.recordId} onSendMessage={handleSendMessage} questionList={generation.relatedSearcheQuestionsList} />}
            </div>
        </>

    };

    return (
        <>
            <div className="mb-[130px] flex w-full min-w-0 flex-col gap-1.5">
                {associatedAssistant && <CustomGptHeader assistant={associatedAssistant} />}
                {
                    displayMessages
                        .filter((message) => message.role !== MessageRole.MODEL_THINKING)
                        .map((message, index) => {
                            if (message.role === 'conversation-created' || message.role === 'add-participant' || message.role === 'conversation-title-updated' || message.role === 'user-removed') {
                                return <ChatEvents key={message.recordId} message={message} />
                            }
                            else if (message.role === MessageRole.USER_CHAT) {
                                return <div
                                    key={message.generations[0].id}
                                    data-chat-request={message.recordId === latestRequestId ? 'latest' : undefined}
                                    className="flex w-full min-w-0 justify-end items-start gap-0.5 mb-2"
                                >
                                    <div className="relative min-w-0 max-w-[90%]">
                                        {messageBubbleUi(message, 0)}
                                        <div className="flex items-center gap-3 mb-1.5 justify-end flex-wrap mr-1">

                                            <span className={`text-xs font-normal ${theme === 'dark' ? 'text-[#F3F3F3]' : 'text-[#585858]'}`}>
                                                You
                                            </span>

                                            <div className={`h-3 w-px ${theme === 'dark' ? 'bg-[#F3F3F3]' : 'bg-[#585858]'}`} />

                                            {(message.generations[0].showMessageTime || message.generations[0].createdDate != null) && (() => {
                                                const d = parseMessageTimeForDisplay(
                                                    message.generations[0].showMessageTime,
                                                    message.generations[0].createdDate
                                                );
                                                if (!d) return null;
                                                return (
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <span className={`text-xs ${theme === 'dark' ? 'text-[#F3F3F3]' : 'text-[#585858]'} cursor-pointer`}>
                                                                    {formatDayAndTime(d)}
                                                                </span>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p>{formatTimeWithAMPM(d)}</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            } else if (
                                message.role === MessageRole.ASSISTANT &&
                                shouldRenderAssistantStreamingRow(
                                    message,
                                    mergeStreamStatusEntriesFromGenerations(
                                        message.generations,
                                        message.activeGenerationIndex ?? 0
                                    ),
                                    isStillGenerating
                                )
                            ) {
                                return (
                                    <AssistantStreamingRow
                                        key={message.recordId}
                                        generation={mergeStreamStatusEntriesFromGenerations(
                                            message.generations,
                                            message.activeGenerationIndex ?? 0
                                        )}
                                    />
                                );
                            }
                            else if (message.role === MessageRole.ASSISTANT && !(message.generations[message.activeGenerationIndex ?? 0].isAnalyzing ?? false)) {
                                return (
                                    <div key={message.recordId} className="flex w-full min-w-0 items-start gap-1 mb-2">
                                        <img
                                            src={seliseDiceLogo}
                                            alt="selise dice logo"
                                        />
                                        <div className="relative min-w-0 flex-1">
                                            {messageBubbleUi(
                                                message,
                                                message.activeGenerationIndex ?? 0,
                                                false,
                                                isLatestApprovalTurn(
                                                    displayMessages.filter(
                                                        (later) => later.role !== MessageRole.MODEL_THINKING
                                                    ),
                                                    index
                                                )
                                            )}
                                        </div>
                                    </div>
                                );
                            }

                            else if (message.role === MessageRole.ASSISTANT_CHAT &&
                                (message.generations[message.activeGenerationIndex ?? 0].content || message.generations[message.activeGenerationIndex ?? 0].isAnalyzing)) {
                                const participant = message.senderId
                                    ? hasParticipantsArray.find(p => p.itemId === message.senderId)
                                    || hasRemovedParticipantsArray.find(p => p.itemId === message.senderId)
                                    : null;
                                // console.log('participant in messageBubbleUi', participant)
                                return (
                                    <div key={message.generations[0].id} className="flex w-full min-w-0 items-start gap-1 mb-2">
                                        {participant?.profileImageUrl ? (
                                            <img

                                                className="mt-[8px] h-7 w-7 shrink-0 rounded-full border-[1px] object-cover"
                                                src={participant.profileImageUrl}
                                                alt={`${participant.firstName}${participant.lastName ? ` ${participant.lastName}` : ''}`}
                                            />
                                        ) : (
                                            <img
                                                className="mt-[8px] h-7 w-7 shrink-0 rounded-full border-[1px]"
                                                src={userParticipant}
                                                alt="participant"
                                            />
                                        )}
                                        <div className="relative min-w-0 flex-1">
                                            {messageBubbleUi(
                                                message,
                                                0,
                                                undefined,
                                                isLatestApprovalTurn(
                                                    displayMessages.filter(
                                                        (later) => later.role !== MessageRole.MODEL_THINKING
                                                    ),
                                                    index
                                                )
                                            )}
                                            {message.senderId && (
                                                <div className="flex items-center gap-3 mb-1.5 justify-start flex-wrap ml-2 mr-1">
                                                    <span className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                        {participant
                                                            ? (() => {
                                                                const firstName = participant.firstName?.trim?.() ?? '';
                                                                const lastName = participant.lastName?.trim?.() ?? '';
                                                                if (firstName || lastName) {
                                                                    return `${firstName}${lastName ? ` ${lastName}` : ''}`;
                                                                }
                                                                const emailPrefix =
                                                                    typeof participant.email === 'string'
                                                                        ? participant.email.split('@')[0]
                                                                        : '';
                                                                return emailPrefix || '';
                                                            })()
                                                            : ''}
                                                    </span>
                                                    <div className={`h-3 w-px ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`} />
                                                    {(() => {
                                                        const gen = message.generations[message.activeGenerationIndex ?? 0];
                                                        const d = parseMessageTimeForDisplay(gen.showMessageTime, gen.createdDate);
                                                        if (!d) return null;
                                                        return (
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <span className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} cursor-pointer`}>
                                                                            {formatDayAndTime(d)}
                                                                        </span>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p>{formatTimeWithAMPM(d)}</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            }
                            else if (message.role === MessageRole.APPROVAL) {
                                const approvalGeneration = message.generations[message.activeGenerationIndex ?? 0];
                                const approvalSurfaces = approvalGeneration?.a2uiSurfaces;
                                if (!approvalSurfaces?.length) return null;
                                return (
                                    <div key={message.recordId} className="flex w-full min-w-0 items-start gap-1 mb-2">
                                        <div className="relative min-w-0 flex-1 pl-9">
                                            <MessageA2uiSurfaces
                                                surfaces={approvalSurfaces}
                                                userMessageId={
                                                    message.userRecordId ??
                                                    approvalGeneration.parentUserGenerationId
                                                }
                                                assistantMessageId={
                                                    approvalGeneration.parentIds?.[0] ?? latestAgentRecordId
                                                }
                                                isLatest={isLatestApprovalTurn(
                                                    displayMessages.filter(
                                                        (later) => later.role !== MessageRole.MODEL_THINKING
                                                    ),
                                                    index
                                                )}
                                            />
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })
                }

                <div ref={messagesEndRef} />
            </div>

            {showScrollControl && (
                <ScrollToBottomControl
                    isStreaming={isStillGenerating}
                    bottomOffset={chatInputHeight - 40}
                    onClick={() => scrollToBottom(true)}
                />
            )}
        </>
    );
};



// card 1: Mention participants
//       1->
//       2-> Fixing ui
//       3-> Adding mention feature in the ui
//       4-> Implementing mention feature with the backend